/**
 * The Activity sidebar entry's unread count: rows logged since this browser
 * last had the Activity page open, per site (plan-frontDeskUx T7). The moment
 * is kept in local storage — a per-browser convenience, so a private window or
 * blocked storage starts from "now" and shows nothing unread rather than the
 * whole log. The count is the server's `total` for the feed's event types
 * since that moment, refreshed by `useActivityLive` like the feed itself.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { activityQueryKey, getApiClient, useActivityLive, useAuthStore } from '@bms/common';
import { activityTypesParam } from './activityModel';

const SEEN_EVENT = 'bms:activity-seen';

// The moments recorded in this page's lifetime: what counts when storage is blocked.
const seenInPage = new Map<string, string>();

function storageKey(siteCode: string): string {
  return `bms.activity.lastSeen.${siteCode}`;
}

/** When this browser last had the Activity page open for `siteCode`, or `null`. */
export function readActivitySeen(siteCode: string): string | null {
  try {
    return window.localStorage.getItem(storageKey(siteCode));
  } catch {
    return null;
  }
}

/** Record that the Activity page is (or just was) on screen for `siteCode`. */
export function markActivitySeen(siteCode: string, at: Date = new Date()): void {
  try {
    window.localStorage.setItem(storageKey(siteCode), at.toISOString());
  } catch {
    // Storage blocked: the count simply restarts from page load.
  }
  seenInPage.set(siteCode, at.toISOString());
  window.dispatchEvent(new CustomEvent(SEEN_EVENT, { detail: { siteCode, at: at.toISOString() } }));
}

function subscribeSeen(onChange: () => void): () => void {
  window.addEventListener(SEEN_EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(SEEN_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}

/** Rows since the Activity page was last open; `null` before the first answer. */
export function useActivityUnread(enabled = true): number | null {
  const siteCode = useAuthStore((state) => state.currentSiteCode);
  useActivityLive(enabled ? siteCode : null);

  // Read from storage (or this page's own record when storage is blocked) and
  // re-read whenever this tab marks the page seen or another tab's storage changes.
  const since = useSyncExternalStore(
    subscribeSeen,
    () => (siteCode ? readActivitySeen(siteCode) ?? seenInPage.get(siteCode) ?? null : null),
    () => null,
  );

  useEffect(() => {
    // First visit in this browser: nothing before now counts as unread.
    if (siteCode && !readActivitySeen(siteCode) && !seenInPage.has(siteCode)) markActivitySeen(siteCode);
  }, [siteCode]);

  const query = useQuery({
    queryKey: [...activityQueryKey(siteCode ?? ''), 'unread', since] as const,
    queryFn: () => getApiClient().getEvents({ type: activityTypesParam('all'), from: since ?? undefined, limit: 1 }),
    enabled: enabled && !!siteCode && !!since,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
  return query.data ? query.data.total : null;
}
